import { ReactNode, createContext, useEffect, useRef, useState } from 'react';
import { clarity } from 'react-microsoft-clarity';
import { useAuth } from 'react-oidc-context';
import { useSearchParams } from 'react-router-dom';
import { Site, useGetUserRoles } from '../hooks';
import { rolesManipulator } from '../utils';

export const AppAuthProviderContext = createContext({
  preservedLocalStorageKeys: [] as string[],
});

const CLARITY_ID = import.meta.env.VITE_CLARITY_ID;
const NODE_ENV = import.meta.env.VITE_NODE_ENV;

interface AppAuthProviderProps {
  children: ReactNode;
  preservedLocalStorageKeys?: string[];
  site?: Site;
}

export const AppAuthProvider = ({
  children,
  preservedLocalStorageKeys = [],
  site = 'app-selector',
}: AppAuthProviderProps) => {
  const startedClarity = useRef(false);
  const auth = useAuth();
  const [user, setUser] = useState<{
    email: string;
    username: string;
    environment: string;
    role: string;
    language: string;
    userAgent: string;
  } | null>(null);
  const userRoles = useGetUserRoles(site);
  const userRole = rolesManipulator.current_user_role;
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    if (
      params.has('state') ||
      params.has('iss') ||
      params.has('code') ||
      params.has('session_state')
    ) {
      timeout = setTimeout(() => {
        params.delete('state');
        params.delete('iss');
        params.delete('code');
        params.delete('session_state');
        setParams(params);
      }, 100);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, []);

  useEffect(() => {
    rolesManipulator.sync_params_and_storage_roles(userRoles);
  }, [params, userRoles]);

  useEffect(() => {
    if (NODE_ENV !== 'dev' && CLARITY_ID && !startedClarity.current) {
      clarity.init(CLARITY_ID);
      clarity.start();
      console.log('Clarity started ✅');
      startedClarity.current = true;
    }
  }, []);

  useEffect(() => {
    if (NODE_ENV !== 'dev') {
      const userAgent = navigator.userAgent;
      const language = navigator.language;
      setUser({
        email: auth?.user?.profile?.email ?? 'guest',
        username: auth?.user?.profile?.name ?? 'guest',
        environment: NODE_ENV,
        role: userRole ?? 'guest',
        userAgent,
        language,
      });
    }

    if (auth?.error) {
      console.log('Auth error:', auth?.error);
    }
  }, [auth, userRole]);

  useEffect(() => {
    if (clarity.hasStarted() && user && NODE_ENV !== 'dev') {
      clarity.identify(user.email, user);
      clarity.setTag('role', user?.role);
      clarity.setTag('environment', user?.environment);
      clarity.setTag('email', user?.email);
      clarity.setTag('username', user?.username);
      clarity.setTag('userAgent', user?.userAgent);
      clarity.setTag('language', user?.language);
      console.log('Updated tag for clarity');
    }
  }, [user]);

  return (
    <AppAuthProviderContext.Provider value={{ preservedLocalStorageKeys }}>
      {children}
    </AppAuthProviderContext.Provider>
  );
};
